import { useEffect, useMemo, useRef, useState } from 'react'
import { useAuth } from 'react-oidc-context'
import { useLocation } from 'react-router-dom'
import { toast } from 'react-toastify'

import { Link } from 'react-router-dom'
import { useSubscriptionDetails } from '../../hooks/apiHooks/subscriptionHooks'
import { useSession } from '../../hooks/storeHooks/useSessionState'
import { useUser } from '../../hooks/storeHooks/useUserState'
import { useClickOutside } from '../../hooks/useClickOutside'
import { OidcUser } from '../../types/oidcUser'
import { analytics } from '../../utils/analytics'
import { getOrganizationId } from '../../utils/getOrganization'
import { formatNumber } from '../../utils/numbersFormatting'
import { AnimatedSpinner } from '../AnimatedSpinner'
import LowBalance from '../LowBalance'

interface LogoutModalProps {
  isOpen: boolean
  onClose: () => void
  onConfirm: () => void
}

function UserDetailsButton({
  firstName,
  lastName,
  email,
  showUserInfo = false,
}: {
  firstName?: string
  lastName?: string
  email?: string
  showUserInfo?: boolean
}) {
  return (
    <div className="flex items-center gap-3 ">
      <div
        className={`flex items-center justify-center text-white font-medium tc-mv-logged-user align-middle rounded-full border-0 ${
          showUserInfo ? 'tc-mv-blur' : ''
        }`}
      >
        {`${firstName?.[0] || email?.[0] || ''}${lastName?.[0] || ''}`}
      </div>
      {showUserInfo && (
        <div className="flex-1 min-w-0 text-white">
          {firstName && lastName && (
            <p className="text-sm font-medium truncate">
              {firstName || ''} {lastName || ''}
            </p>
          )}
          <p className="text-xs truncate">{email}</p>
        </div>
      )}
    </div>
  )
}

function LogoutModal({ isOpen, onClose, onConfirm }: LogoutModalProps) {
  if (!isOpen) return null

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div
        className="tc-mv-blur p-6 max-w-sm w-full mx-4"
        data-testid="logout-confirmation-dialog"
      >
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-white text-xl font-semibold">Confirm Logout</h2>
          <button onClick={onClose} className="text-gray-500 hover:text-white">
            <i className="fa-solid fa-xmark text-lg" />
          </button>
        </div>
        <p className="text-white mb-6">Are you sure you want to log out?</p>
        <div className="flex justify-end gap-4">
          <button
            onClick={onClose}
            className="btn text-gray-200 hover:bg-black hover:bg-opacity-20"
          >
            Cancel
          </button>
          <button
            onClick={onConfirm}
            className="btn bg-red-600 text-white rounded-md hover:bg-red-700"
          >
            Logout
          </button>
        </div>
      </div>
    </div>
  )
}

interface LoginButtonV2Props {
  className?: string
  signInText?: string
  returnUrl?: string
}

export default function LoginButtonV2({
  className,
  signInText,
  returnUrl,
}: LoginButtonV2Props) {
  const location = useLocation()
  const { isAuthenticated, user, signinRedirect, removeUser, signoutRedirect } =
    useAuth()

  const [showMenu, setShowMenu] = useState(false)
  const [showLogoutModal, setShowLogoutModal] = useState(false)
  const menuRef = useRef<HTMLDivElement>(null)

  const { isLoading, user: storedUser } = useUser()
  const {
    secondsBalance,
    setSecondsBalance,
    setCreditBalance,
    setIsSubsDataLoading,
  } = useSession()

  const orgId = getOrganizationId(user as OidcUser)

  const { data: subscriptionDetails, isLoading: isBalanceLoading } =
    useSubscriptionDetails({ organization_id: orgId }, isAuthenticated)

  useEffect(() => {
    setIsSubsDataLoading(isBalanceLoading)
  }, [isBalanceLoading])

  const isLowBalance = useMemo(() => {
    return secondsBalance < 600
  }, [secondsBalance])

  const minutesBalance = useMemo(() => {
    return secondsBalance ? Math.floor(secondsBalance / 60) : 0
  }, [secondsBalance])

  useClickOutside(menuRef, () => setShowMenu(false))

  const handleSignIn = async () => {
    await signinRedirect({
      state: {
        returnUrl: returnUrl || `${location.pathname}${location.search}`,
      },
    })
  }

  const handleLogout = async () => {
    try {
      await signoutRedirect()
      void removeUser()
      setShowLogoutModal(false)
      toast.success('Successfully logged out')
    } catch (error) {
      console.error(error)
      toast.error('Failed to log out')
    }
  }

  useEffect(() => {
    if (subscriptionDetails) {
      setIsSubsDataLoading(false)
      const { credit_usage } = subscriptionDetails
      setSecondsBalance(
        credit_usage.subscription_details.balance +
          credit_usage.pack_details.balance,
      )
      setCreditBalance(
        credit_usage.subscription_details.creditBalance +
          credit_usage.pack_details.creditBalance,
      )
    }
  }, [subscriptionDetails])

  useEffect(() => {
    if (user && subscriptionDetails) {
      const { credit_usage, billingDetails } = subscriptionDetails
      const params: Record<string, string | number> = {
        role: user.profile.role as string,
        idp: user.profile.idp as string,
        is_subscribed:
          credit_usage.subscription_details.status === 'subscribed' &&
          billingDetails.paymentType !== 'Free'
            ? 'yes'
            : 'no',
        has_purchased_packs: credit_usage.pack_details.lastCheckoutAt
          ? 'yes'
          : 'no',
        available_seconds:
          credit_usage.subscription_details.balance +
          credit_usage.pack_details.balance,
        timestamp_user_account_creation: storedUser?.accountCreatedAt || 0,
      }

      if (billingDetails.userConvertedToPaidAt) {
        params.timestamp_user_converted_to_paid =
          billingDetails.userConvertedToPaidAt
      }

      if (
        user.profile.email?.toLowerCase().includes('touchcast') ||
        user.profile.email?.toLowerCase().includes('napster') ||
        user.profile.email?.toLowerCase().includes('theinfinitereality')
      ) {
        params.is_employee = 'yes'
      }

      analytics.setUser(user.profile.sub, params)
    }
  }, [user, subscriptionDetails, storedUser])

  const handleConfirmLogout = () => {
    void handleLogout()
  }
  if (isLoading) {
    return (
      <button
        className="flex items-center space-x-2 p-2 rounded-full relative"
        disabled
      >
        <AnimatedSpinner className="text-white" />
      </button>
    )
  }

  if (isAuthenticated) {
    return (
      <>
        {!isBalanceLoading && isLowBalance && (
          <LowBalance balance={minutesBalance} />
        )}
        <div ref={menuRef} className="relative">
          <button
            onClick={() => setShowMenu(!showMenu)}
            className="flex tc-mv-blur items-center space-x-2 rounded-full h-10 w-10 border-0"
            title="User Menu"
            data-testid="user-menu-button"
          >
            <UserDetailsButton
              firstName={storedUser?.firstName || ''}
              lastName={storedUser?.lastName || ''}
              email={storedUser?.email || ''}
            />
          </button>

          {showMenu && (
            <div className="absolute right-0 mt-6 w-80  shadow-lg z-50 tc-mv-blur rounded-regular p-4">
              <UserDetailsButton
                firstName={storedUser?.firstName || ''}
                lastName={storedUser?.lastName || ''}
                email={storedUser?.email || ''}
                showUserInfo
              />

              <div className="my-4 border-t border-gray-500 w-full" />
              <button
                onClick={() => {
                  setShowMenu(false)
                  window.location.href = '/generate-twin'
                }}
                className="w-full px-2 h-10 rounded-md text-sm  text-gray-200 hover:bg-black hover:bg-opacity-20 flex items-center gap-2 transition-colors duration-150"
              >
                <i className="fa-solid fa-user-plus text-[16px]" />
                Digital twin
              </button>

              <div className="my-4 border-t border-gray-500 w-full" />

              <div className="flex flex-col gap-4">
                <div className="flex text-sm items-center gap-2 justify-between font-normal">
                  {isLowBalance ? (
                    <div className="text-red-600">
                      Low balance: {formatNumber(minutesBalance)}
                    </div>
                  ) : (
                    <div className="text-white">
                      Available minutes: {formatNumber(minutesBalance)}
                    </div>
                  )}

                  <a
                    href="/pricing"
                    rel="external"
                    className="btn btn-success btn-small"
                  >
                    Buy minutes
                  </a>
                </div>
              </div>

              <div className="my-4 border-t border-gray-500 w-full" />
              <button
                onClick={() => {
                  setShowMenu(false)
                  window.location.href = '/account'
                }}
                className="w-full px-2 h-10 rounded-md text-sm  text-gray-200 hover:bg-black hover:bg-opacity-20 flex items-center gap-2 transition-colors duration-150"
              >
                <i className="fa-solid fa-user  text-[16px]" />
                My account
              </button>
              <button
                onClick={() => {
                  setShowMenu(false)
                  window.location.href = '/pricing'
                }}
                className="w-full px-2 h-10 rounded-md text-sm  text-gray-200 hover:bg-black hover:bg-opacity-20 flex items-center gap-2 transition-colors duration-150"
              >
                <i className="fa-solid fa-tag text-[16px]" />
                Pricing
              </button>
              <Link
                to="https://napstercompanion.zendesk.com/hc/en-us"
                target="_blank"
                rel="noopener noreferrer"
                className="w-full px-2 h-10 rounded-md text-sm  text-gray-200 hover:bg-black hover:bg-opacity-20 flex items-center gap-2 transition-colors duration-150"
              >
                <i className="fa-solid fa-question" />
                Help
              </Link>
              <button
                onClick={() => {
                  setShowMenu(false)
                  setShowLogoutModal(true)
                }}
                className="w-full px-2 h-10 rounded-md text-sm  text-gray-200 hover:bg-black hover:bg-opacity-20  flex items-center gap-2 transition-colors duration-150"
              >
                <i className="fa-solid fa-arrow-right-from-bracket text-[16px]" />
                Sign out
              </button>
            </div>
          )}

          <LogoutModal
            isOpen={showLogoutModal}
            onClose={() => setShowLogoutModal(false)}
            onConfirm={handleConfirmLogout}
          />
        </div>
      </>
    )
  }

  const handleClick = () => {
    void handleSignIn()
  }

  return (
    <button
      onClick={handleClick}
      className={
        className ||
        'flex items-center space-x-1 px-4 py-2 rounded-md border border-transparent text-sm font-medium text-white focus:outline-none whitespace-nowrap'
      }
      title="Sign In"
    >
      {signInText || 'Sign In'}
      {!signInText && (
        <i className="fa-solid fa-arrow-right-to-bracket ml-2 text-lg" />
      )}
    </button>
  )
}
